import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArmStateChip,
  Button,
  Card,
  Chip,
  EmptyState,
  ProviderTag,
  Skeleton,
  StatusChip,
} from '../components/ui'
import {
  useCredentials,
  useDoors,
  useEvents,
  useLocationTree,
  useMatchSuggestions,
  usePersons,
  useSystems,
  useUnknownCredentials,
  useZones,
} from '../api/hooks'
import { api } from '../api/client'
import type { AccessEvent, MatchSuggestion, UnknownCredential } from '../api/types'
import { MappingDashboardCallout } from '../components/features/MappingCallout'
import { EventListRow, useEventPresentation } from '../components/features/EventRows'
import { EventDetailModal } from './Events'
import { useAuthStore } from '../stores/auth'
import { relativeTime } from '../lib/format'
import { locationNames, locationPaths } from '../lib/locations'

function Stat({ label, value }: { label: string; value: string | number | undefined }) {
  return (
    <div className="text-right">
      <p className="text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">{label}</p>
      <p className="tnum mt-0.5 font-display text-2xl text-ostrel-ink">{value ?? '–'}</p>
    </div>
  )
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** Match-suggestion payloads are still stabilizing on the backend — read defensively. */
function suggestionView(s: MatchSuggestion) {
  const name = str(s.person_name) ?? str(s.display_name) ?? str(s.name) ?? 'Suggested match'
  const rawConf = typeof s.confidence === 'number' ? s.confidence : null
  const confidence = rawConf === null ? null : Math.round(rawConf <= 1 ? rawConf * 100 : rawConf)
  const rationale = str(s.rationale) ?? str(s.reason) ?? null
  const credential = str(s.unknown_credential) ?? str(s.unknown_credential_id) ?? null
  return { name, confidence, rationale, credential }
}

function credKind(c: UnknownCredential): string {
  return c.code_raw && c.code_raw.includes(':') ? 'Tag' : 'Code'
}

export default function OverviewPage() {
  const customer = useAuthStore((s) => s.customer)
  const scope = customer?.unique_id ?? 'none'
  const queryClient = useQueryClient()

  const systems = useSystems()
  const tree = useLocationTree()
  const doors = useDoors()
  const zones = useZones()
  const persons = usePersons()
  const credentials = useCredentials()
  // Computed once per mount -- a fresh ISO string every render would change
  // the query key each time and loop refetches.
  const [since24h] = useState(() => new Date(Date.now() - 24 * 3600 * 1000).toISOString())
  const events24h = useEvents({ since: since24h })
  const recentEvents = useEvents()
  // Dashboard rows expand into the Events page's detail modal, compact
  // variant — same mechanic, fewer fields, no raw dump.
  const [selectedEvent, setSelectedEvent] = useState<AccessEvent | null>(null)
  const { systemProviders, systemName, personName, locationName, deviceName, zoneName } = useEventPresentation()
  const systemProvider = (uid: string | null) => (uid ? (systemProviders.get(uid) ?? null) : null)
  const unknownCreds = useUnknownCredentials('open')
  const suggestions = useMatchSuggestions('pending')

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  const paths = tree.data ? locationPaths(tree.data) : new Map<string, string>()
  const locNames = tree.data ? locationNames(tree.data) : new Map<string, string>()

  const events24hCount = events24h.data
    ? `${events24h.data.items.length}${events24h.data.next_cursor ? '+' : ''}`
    : undefined

  const zoneCounts = { armed: 0, part: 0, disarmed: 0 }
  for (const z of zones.data ?? []) {
    if (z.arm_state.startsWith('armed')) zoneCounts.armed += 1
    else if (z.arm_state === 'partial') zoneCounts.part += 1
    else zoneCounts.disarmed += 1
  }

  const attentionCount = (unknownCreds.data?.length ?? 0) + (suggestions.data?.length ?? 0)
  const suggestionFor = (credUids: string[]) =>
    (suggestions.data ?? []).find((s) => credUids.includes(suggestionView(s).credential ?? ''))
  const accept = useMutation({ mutationFn: api.acceptMatchSuggestion, onSuccess: invalidate })
  const reject = useMutation({ mutationFn: api.rejectMatchSuggestion, onSuccess: invalidate })
  const mutating = accept.isPending || reject.isPending

  const looseSuggestions = (suggestions.data ?? []).filter(
    (s) =>
      !suggestionView(s).credential ||
      !(unknownCreds.data ?? []).some((c) => c.sightings.some((x) => x.unique_id === suggestionView(s).credential)),
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">Overview</p>
          <h1 className="mt-1 font-display text-3xl text-ostrel-ink">System health</h1>
        </div>
        <div className="flex gap-6">
          <Stat label="Doors" value={doors.data?.length} />
          <Stat label="People" value={persons.data?.length} />
          <Stat label="Credentials" value={credentials.data?.length} />
          <Stat label="Events 24h" value={events24hCount} />
        </div>
      </div>

      {systems.data && systems.data.length > 0 && <MappingDashboardCallout systems={systems.data} />}

      {systems.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : systems.isError ? (
        <EmptyState title="Could not load systems" hint={systems.error.message} />
      ) : systems.data.length === 0 ? (
        <EmptyState title="No systems connected" hint="Add a system from the Systems page." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {systems.data.map((s) => (
            <div key={s.unique_id} className="rounded-sm border border-ostrel-line bg-ostrel-card p-3.5">
              <div className="flex items-center gap-2">
                <ProviderTag provider={s.provider} />
                <p className="truncate text-sm font-medium text-ostrel-ink">
                  <Link to={`/systems/${s.unique_id}`} className="hover:text-ostrel-accent">
                    {s.name}
                  </Link>
                </p>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <StatusChip status={s.status} />
                <span className="tnum truncate text-[11px] text-ostrel-ink-muted">
                  {s.status_detail || (s.enabled ? 'enabled' : 'disabled')}
                </span>
              </div>
              <div
                className={`mt-2.5 h-0.5 w-full rounded-full ${
                  s.status === 'active'
                    ? 'bg-ostrel-ok'
                    : s.status === 'auth_failed'
                      ? 'bg-ostrel-danger'
                      : s.status === 'needs_sites' || s.status === 'duplicate'
                        ? 'bg-ostrel-warn'
                        : 'bg-ostrel-line'
                }`}
              />
              <div className="tnum mt-2 flex items-center justify-between text-[11px] text-ostrel-ink-muted">
                <span>{s.capabilities.length} capabilities</span>
                <span>
                  {s.sync_policy?.incremental_seconds
                    ? `polling ${s.sync_policy.incremental_seconds}s`
                    : (s.gateway ?? '—')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Card
        title="Alarm zones"
        action={
          <span className="flex items-center gap-4">
            <span className="tnum text-[11px] text-ostrel-ink-muted">
              {zoneCounts.armed} armed · {zoneCounts.part} part · {zoneCounts.disarmed} disarmed
            </span>
            <Link
              to="/locations"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              Locations →
            </Link>
          </span>
        }
      >
        {zones.isPending ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : zones.isError ? (
          <EmptyState title="Could not load zones" hint={zones.error.message} />
        ) : zones.data.length === 0 ? (
          <EmptyState title="No alarm zones" hint="Zones appear once an alarm-capable system is active." />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {zones.data.map((z) => (
              <div key={z.unique_id} className="rounded-sm border border-ostrel-line bg-ostrel-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ostrel-ink">{z.name}</p>
                    <p className="mt-0.5 truncate text-[11px] text-ostrel-ink-muted">
                      {z.locations.length > 0
                        ? (paths.get(z.locations[0]) ?? locNames.get(z.locations[0]) ?? z.locations[0])
                        : 'No locations'}
                    </p>
                  </div>
                  <ArmStateChip state={z.arm_state} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="self-start">
          <div className="-mt-1 mb-3 flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-ostrel-gold" />
            <h2 className="font-display text-lg text-ostrel-ink">Needs attention</h2>
            {attentionCount > 0 && (
              <span className="tnum rounded-sm border border-ostrel-gold/50 bg-ostrel-gold/10 px-1.5 text-[10px] font-semibold text-ostrel-gold">
                {attentionCount}
              </span>
            )}
          </div>
          {unknownCreds.isPending || suggestions.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ) : attentionCount === 0 ? (
            <EmptyState title="Nothing needs attention" hint="Unknown credentials and AI matches land here." />
          ) : (
            <ul className="flex flex-col gap-4">
              {(unknownCreds.data ?? []).map((c) => {
                const suggestion = suggestionFor(c.sightings.map((x) => x.unique_id))
                const view = suggestion ? suggestionView(suggestion) : null
                return (
                  <li key={c.code} className="border-b border-ostrel-line pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <code className="tnum rounded-sm border border-ostrel-line bg-ostrel-sand px-1.5 py-0.5 font-mono text-xs text-ostrel-ink">
                        {c.code}
                      </code>
                      <Chip tone="muted" label={credKind(c)} />
                      <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
                        seen {c.seen_count}×
                      </span>
                    </div>
                    <p className="tnum mt-1.5 text-[11px] text-ostrel-ink-muted">
                      {c.sightings.map((x) => x.system_name).join(' · ') || 'Unknown system'}
                      {' · '}
                      {relativeTime(c.last_seen_at)}
                    </p>
                    {view && (
                      <div className="mt-2 rounded-sm border border-ostrel-line bg-ostrel-sand/60 p-2.5">
                        <div className="flex items-center gap-2">
                          <Chip tone="gold" label="AI match" />
                          <p className="min-w-0 flex-1 truncate text-sm font-medium text-ostrel-ink">
                            {view.name}
                          </p>
                          {view.confidence !== null && (
                            <span className="tnum text-xs font-semibold text-ostrel-ink">{view.confidence}%</span>
                          )}
                        </div>
                        {view.confidence !== null && (
                          <div className="mt-1.5 h-1 w-full rounded-full bg-ostrel-line">
                            <div
                              className={`h-1 rounded-full ${view.confidence >= 80 ? 'bg-ostrel-ok' : 'bg-ostrel-gold'}`}
                              style={{ width: `${Math.min(100, view.confidence)}%` }}
                            />
                          </div>
                        )}
                        {view.rationale && (
                          <p className="mt-1.5 text-[11px] leading-relaxed text-ostrel-ink-muted">
                            {view.rationale}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="mt-2.5">
                      <Link
                        to={`/systems/${(c.sightings.find((x) => x.status === 'open') ?? c.sightings[0]).system}/mapping`}
                        className="text-[11px] font-semibold text-ostrel-gold hover:text-ostrel-accent"
                      >
                        Review in workbench →
                      </Link>
                    </div>
                  </li>
                )
              })}
              {looseSuggestions.map((s) => {
                const view = suggestionView(s)
                return (
                  <li key={s.unique_id} className="border-b border-ostrel-line pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <Chip tone="gold" label="AI match" />
                      <p className="min-w-0 flex-1 truncate text-sm font-medium text-ostrel-ink">{view.name}</p>
                      {view.confidence !== null && (
                        <span className="tnum text-xs font-semibold text-ostrel-ink">{view.confidence}%</span>
                      )}
                    </div>
                    {view.rationale && (
                      <p className="mt-1.5 text-[11px] leading-relaxed text-ostrel-ink-muted">{view.rationale}</p>
                    )}
                    <div className="mt-2.5 flex gap-2">
                      <Button variant="navy" size="sm" disabled={mutating} onClick={() => accept.mutate(s.unique_id)}>
                        Accept
                      </Button>
                      <Button variant="ghost" size="sm" disabled={mutating} onClick={() => reject.mutate(s.unique_id)}>
                        Reject
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Recent events"
          className="self-start"
          action={
            <Link
              to="/events"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              View all →
            </Link>
          }
        >
          {recentEvents.isPending ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : recentEvents.isError ? (
            <EmptyState title="Could not load events" hint={recentEvents.error.message} />
          ) : recentEvents.data.items.length === 0 ? (
            <EmptyState title="No events yet" hint="Events appear once systems start syncing." />
          ) : (
            <ul className="flex flex-col divide-y divide-ostrel-line">
              {recentEvents.data.items.slice(0, 12).map((e) => (
                <EventListRow key={e.unique_id} event={e} onSelect={setSelectedEvent} />
              ))}
            </ul>
          )}
        </Card>
      </div>

      <EventDetailModal
        event={selectedEvent}
        personName={personName}
        locationName={locationName}
        deviceName={deviceName}
        zoneName={zoneName}
        systemProvider={systemProvider}
        systemName={systemName}
        compact
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  )
}
